import {
  isSupportedCountry,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js/max';

export type PhoneValidationStatus = 'valid' | 'invalid' | 'unverified';

export interface PhoneValidationResult {
  status: PhoneValidationStatus;
  input: string;
  /** E.164 quando a biblioteca conseguiu interpretar; forma limpa no caso não verificável. */
  number?: string;
  formatted?: string;
  country?: CountryCode;
  mobile?: boolean;
  reason?: 'empty' | 'unsupported_country' | 'missing_country' | 'impossible' | 'not_valid';
}

function rawText(value: unknown): string {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  return String(value).trim();
}

function cleanPhone(value: string): string {
  const hasInternationalPrefix = /^\s*\+/.test(value);
  const digits = value.replace(/\D/g, '');
  return digits ? `${hasInternationalPrefix ? '+' : ''}${digits}` : '';
}

function countryCode(value: string | undefined): CountryCode | undefined {
  const normalized = value?.trim().toUpperCase();
  return normalized && isSupportedCountry(normalized) ? normalized : undefined;
}

/** Valida, classifica e converte um telefone para E.164 usando os metadados completos. */
export function validatePhone(value: unknown, defaultCountry?: string): PhoneValidationResult {
  const input = rawText(value);
  const cleaned = cleanPhone(input);
  if (!cleaned) return { status: 'invalid', input, reason: 'empty' };
  if (cleaned.replace(/\D/g, '').length < 6) {
    return { status: 'invalid', input, reason: 'impossible' };
  }

  const country = countryCode(defaultCountry);
  if (!cleaned.startsWith('+') && defaultCountry && !country) {
    return {
      status: 'unverified',
      input,
      number: cleaned,
      reason: 'unsupported_country',
    };
  }
  if (!cleaned.startsWith('+') && !country) {
    return { status: 'unverified', input, number: cleaned, reason: 'missing_country' };
  }

  try {
    const parsed = parsePhoneNumberFromString(cleaned, country);
    if (!parsed || !parsed.isPossible()) {
      return { status: 'invalid', input, reason: 'impossible' };
    }

    const type = parsed.getType();
    const result: PhoneValidationResult = {
      status: parsed.isValid() ? 'valid' : 'unverified',
      input,
      number: parsed.number,
      formatted: parsed.formatNational(),
      country: parsed.country,
      mobile: type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE',
    };
    if (result.status === 'unverified') result.reason = 'not_valid';
    return result;
  } catch {
    return { status: 'invalid', input, reason: 'impossible' };
  }
}
